'use client';

import { Box, Stack, Typography } from '@mui/material';
import { radius } from '@odys/design-system/tokens/radius';
import Image from 'next/image';

import type { ReactNode } from 'react';

/** Côté de la vignette carrée, en px. */
const THUMB_SIZE = 64;

/** Opacité d'un item incomplet — la même que sur la card. */
const DIMMED_OPACITY = 0.55;

export type CatalogRowProps = {
    /** URL de la vignette. Absente, un aplat gris tient la place. */
    imageUrl?: string;
    badge?: ReactNode;
    /** Sur une ligne, coupé au-delà : la hauteur est contrainte. */
    title: string;
    meta?: string | null;
    price?: ReactNode;
    chips?: ReactNode;
    /** Action de fin de ligne, hors du contenu estompé. */
    action?: ReactNode;
    href?: string | null;
    /** Appelé au clic sur la ligne quand elle est un lien. */
    onClick?: () => void;
    dimmed?: boolean;
    'data-testid'?: string;
};

/**
 * Ligne d'un item de catalogue, en vue liste.
 *
 * Même contrat que `CatalogCard`, autre densité : le titre tient sur une seule
 * ligne, et la vignette est un carré de 64 px à gauche.
 *
 * La vignette passe par `next/image`. La ligne Produit utilisait un
 * `backgroundImage` CSS, donc sans redimensionnement ni format moderne, pour le
 * même carré de 64 px que la ligne Itinéraire servait déjà optimisé — une
 * divergence sans intention.
 */
export function CatalogRow({
    imageUrl,
    badge,
    title,
    meta,
    price,
    chips,
    action,
    href,
    onClick,
    dimmed = false,
    'data-testid': dataTestId,
}: CatalogRowProps) {
    const isLink = href !== undefined && href !== null;
    const linkProps = isLink
        ? ({
              component: 'a',
              href,
              target: '_blank',
              rel: 'noopener noreferrer',
              onClick,
          } as const)
        : {};

    return (
        <Stack
            {...linkProps}
            direction="row"
            spacing={2}
            data-testid={dataTestId}
            data-incomplete={dimmed ? 'true' : undefined}
            sx={{
                alignItems: 'center',
                p: 1.5,
                borderRadius: radius.md,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                textDecoration: 'none',
                color: 'inherit',
                cursor: isLink ? 'pointer' : 'default',
                transition: 'background-color .15s ease',
                ...(isLink ? { '&:hover': { bgcolor: 'action.hover' } } : {}),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flex: 1,
                    minWidth: 0,
                    opacity: dimmed ? DIMMED_OPACITY : 1,
                    transition: 'opacity .15s ease',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: THUMB_SIZE,
                        height: THUMB_SIZE,
                        borderRadius: radius.sm,
                        flexShrink: 0,
                        overflow: 'hidden',
                        bgcolor: 'grey.200',
                    }}
                >
                    {imageUrl !== undefined && (
                        <Image
                            src={imageUrl}
                            alt=""
                            fill
                            sizes={`${THUMB_SIZE}px`}
                            style={{ objectFit: 'cover' }}
                        />
                    )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {badge !== undefined && <Box sx={{ mb: 0.5 }}>{badge}</Box>}
                    <Typography
                        variant="subtitle1"
                        noWrap
                        sx={{ fontWeight: 600, lineHeight: 1.25 }}
                    >
                        {title}
                    </Typography>
                    {meta !== undefined && meta !== null && (
                        <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
                            {meta}
                        </Typography>
                    )}
                    {price}
                    {chips !== undefined && <Box sx={{ mt: 0.5 }}>{chips}</Box>}
                </Box>
            </Box>
            {action}
        </Stack>
    );
}
